import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { consoleService } from '../../services/consoleService';
import { DashboardOverview, GameTitle } from '../../types/dashboard';
import './Dashboard.scss';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await consoleService.getDashboardOverview();
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getGameInfo = (game: GameTitle) => {
    const gameMap = {
      [GameTitle.LUDO]: { name: 'Ludo', icon: '🎲', className: 'ludo' },
      [GameTitle.QUIZ]: { name: 'Quiz', icon: '🧠', className: 'quiz' },
      [GameTitle.SOLITAIRE]: { name: 'Solitaire', icon: '🃏', className: 'solitaire' }
    };
    return gameMap[game];
  };

  const getGameRoute = (game: GameTitle) => {
    switch(game) {
      case GameTitle.QUIZ: return '/quiz-management';
      case GameTitle.LUDO: return '/ludo-settings';
      case GameTitle.SOLITAIRE: return '/solitaire-settings';
      default: return '/container-settings';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title">Dashboard</h1>
          </div>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title">Dashboard</h1>
          </div>
        </div>
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button className="btn" onClick={() => fetchDashboardData()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title">Dashboard</h1>
          </div>
        </div>
        <div className="alert alert-info">
          No dashboard data available.
        </div>
      </div>
    );
  }

  const { userStatistics, gamesPlayed } = dashboardData;
  const inactiveUsers = userStatistics.totalUsers - userStatistics.onlineUsers;

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
      </div>

      {/* User Statistics */}
      <div className="section">
        <h2 className="section-title">User Statistics</h2>
        <div className="stats-grid">
          <div className="stat-card-modern total">
            <div className="card-top">
              <div className="stat-icon-circle">👥</div>
              <div className="stat-trend up">
                <span>↑</span>
              </div>
            </div>
            <div className="stat-number">{userStatistics.totalUsers.toLocaleString()}</div>
            <div className="stat-title">Total Users</div>
          </div>

          <div className="stat-card-modern online">
            <div className="card-top">
              <div className="stat-icon-circle">🟢</div>
              <div className="stat-live-dot"></div>
            </div>
            <div className="stat-number">{userStatistics.onlineUsers.toLocaleString()}</div>
            <div className="stat-title">Online Now</div>
          </div>

          <div className="stat-card-modern offline">
            <div className="card-top">
              <div className="stat-icon-circle">💤</div>
            </div>
            <div className="stat-number">{inactiveUsers.toLocaleString()}</div>
            <div className="stat-title">Offline</div>
          </div>
        </div>
      </div>

      {/* Games */}
      <div className="section">
        <h2 className="section-title">Game Management</h2>
        <div className="games-grid">
          {gamesPlayed.gameStatistics.map((gameStats) => {
            const gameInfo = getGameInfo(gameStats.game);
            return (
              <div key={gameStats.game} className={`game-card ${gameInfo.className}`}>
                <div className="game-header">
                  <div className="game-icon-wrapper">
                    {gameInfo.icon}
                  </div>
                  <div className="game-info">
                    <h3 className="game-name">{gameInfo.name}</h3>
                    <p className="game-subtitle">Game Settings</p>
                  </div>
                </div>
                
                <div className="game-action">
                  <button 
                    className="game-settings-btn"
                    onClick={() => navigate(getGameRoute(gameStats.game))}
                  >
                    <span className="btn-icon">⚙️</span>
                    <span className="btn-text">Manage Settings</span>
                    <span className="btn-arrow">→</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
